/**
 * @license
 * SKALE bite.ts
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

/**
 * @file encrypt.ts
 * @copyright SKALE Labs 2025-Present
 */

import {
    encryptMessage as encryptRawMessage,
    encryptMessageDualKey as encryptRawMessageDualKey,
    encryptMessageMockup as encryptRawMessageMockup
} from '@skalenetwork/t-encrypt';
import { encode } from '@ethereumjs/rlp';
import {logger} from "../utils/logger";
import * as utils from '../utils/helper';
import * as constants from '../utils/constants';


export interface Transaction {
    to: string;
    data: string;
    gasLimit?: string;
}

/**
 * Encrypts a transaction using the real BLS key.
 *
 * @param {Transaction} tx - The transaction object.
 * @param {utils.CommitteeInfo[]} committees - The committees info object.
 * @param {string} [aadTE] - Optional TE Additional Authenticated Data.
 * @param {string} [aadAES] - Optional AES Additional Authenticated Data.
 * @returns {Promise<Transaction>} - A promise with encrypted transaction.
 *
 */
export async function encryptTransaction(
    tx: Transaction,
    committees: utils.CommitteeInfo[],
    aadTE?: string,
    aadAES?: string
): Promise<Transaction> {
    try {
        const validatedTx = validateAndExtractTransactionFields(tx);
        const txTo = validatedTx.to;
        const txData = validatedTx.data;

        // RLP encode data and to fields
        const rlpEncodedData = rlpEncodeTransactionData(txTo, txData);

        const sanitizedAADAES = aadAES ? utils.remove0xPrefixIfNeeded(aadAES) : undefined;
        const sanitizedAADTE = aadTE ? utils.remove0xPrefixIfNeeded(aadTE) : undefined;

        if (sanitizedAADAES) utils.validateHexString(sanitizedAADAES);
        if (sanitizedAADTE) utils.validateHexString(sanitizedAADTE);

        const encryptedData = await encryptMessage(rlpEncodedData, committees, sanitizedAADTE, sanitizedAADAES);

        // Set default gasLimit if not set
        const biteGasLimit = tx.gasLimit ?? constants.DEFAULT_GAS_LIMIT;

        return {
            ...tx,
            data: encryptedData,
            to: constants.BITE_ADDRESS,
            gasLimit: biteGasLimit
        };
    } catch (error) {
        logger.error('Error encrypting transaction:', error);
        throw error;
    }
}

/**
 * Encrypts a transaction using mock encryption.
 *
 * @param {Transaction} tx - The transaction object.
 * @returns {Promise<Transaction>} - A promise with encrypted transaction.
 *
 */
export async function encryptTransactionMockup(tx: Transaction): Promise<Transaction> {
    try {
        const validatedTx = validateAndExtractTransactionFields(tx);
        const txTo = validatedTx.to;
        const txData = validatedTx.data;

        // RLP encode data and to fields
        const rlpEncodedData = rlpEncodeTransactionData(txTo, txData);

        const encryptedData = await encryptMessageMockup(rlpEncodedData);
      
        // Set default gasLimit if not set
        const biteGasLimit = tx.gasLimit ?? constants.DEFAULT_GAS_LIMIT;
      
        return {
            ...tx,
            data: encryptedData,
            to: constants.BITE_ADDRESS,
            gasLimit: biteGasLimit
        };
    } catch (error) {
        logger.error('Error encrypting transaction (mockup):', error);
        throw error;
    }
}

/**
 * Encrypts a raw hex-encoded message using the real BLS key(s).
 *
 * @param {string} message - The message to encrypt, as a hex string (with or without 0x prefix).
 * @param {utils.CommitteeInfo[]} committees - The committees info object.
 * @param {string} [aadTE] - Optional TE Additional Authenticated Data, as a hex string.
 * @param {string} [aadAES] - Optional AES Additional Authenticated Data, as a hex string.
 * @returns {Promise<string>} - The encrypted message.
 */
export async function encryptMessage(
    message: string,
    committees: utils.CommitteeInfo[],
    aadTE?: string,
    aadAES?: string
): Promise<string> {
    try {
        const data = utils.remove0xPrefixIfNeeded(message);
        utils.validateHexString(data);

        const sanitizedAADTE = aadTE ? utils.remove0xPrefixIfNeeded(aadTE) : undefined;
        const sanitizedAADAES = aadAES ? utils.remove0xPrefixIfNeeded(aadAES) : undefined;
        
        if (sanitizedAADAES) {
            utils.validateHexString(sanitizedAADAES);
        }
        if (sanitizedAADTE) {
            utils.validateHexString(sanitizedAADTE);
        }

        if (committees.length === 1) {
            const publicKeyResponse = committees[0];
            const encryptedRawMessage = await encryptRawMessage(data, publicKeyResponse.commonBLSPublicKey, sanitizedAADTE, sanitizedAADAES);

            // RLP encode epochID and encrypted message
            const rlpEncodedResult = rlpEncodeMessageData([publicKeyResponse.epochId, Buffer.from(encryptedRawMessage, 'hex')]);
            return `0x${rlpEncodedResult}`;
        } else {
            const encryptedRawMessage = await encryptRawMessageDualKey(data, committees[0].commonBLSPublicKey,
                committees[1].commonBLSPublicKey, sanitizedAADTE, sanitizedAADAES);

            // RLP encode epochID and encrypted message
            const rlpEncodedResult = rlpEncodeMessageData([committees[0].epochId, Buffer.from(encryptedRawMessage, 'hex')]);
            return `0x${rlpEncodedResult}`;
        }
    } catch (error) {
        logger.error('Error encrypting message:', error);
        throw error;
    }
}

/**
 * Encrypts a raw hex-encoded message using mock encryption.
 *
 * @param {string} message - The message to encrypt, as a hex string (with or without 0x prefix).
 * @returns {Promise<string>} - The encrypted message.
 */
export async function encryptMessageMockup(
    message: string
): Promise<string> {
    try {
        const data = utils.remove0xPrefixIfNeeded(message);

        if (!/^[0-9a-fA-F]*$/.test(data) || data.length % 2 !== 0) {
            throw new Error("Invalid input: message must be valid hex and even length");
        }

        const encryptedRawMessage = await encryptRawMessageMockup(data);
        const epochId = 0;

        // RLP encode epochID and encrypted message
        const rlpEncodedResult = rlpEncodeMessageData([epochId, Buffer.from(encryptedRawMessage, 'hex')]);
        return `0x${rlpEncodedResult}`;
    } catch (error) {
        logger.error('Error encrypting message:', error);
        throw error;
    }
}

/**
 * Validates a transaction object for encryption and extracts the fields to be encrypted.
 * @param {object} tx -  The transaction object containing a 'data' and 'to' fields (hex string).
 * @returns {{ data: string, to: string}} An object with the 'data' and 'to' fields properly validated
 * and formatted as hexadecimal strings without the '0x' prefix.
 */
function validateAndExtractTransactionFields(tx: Transaction): Transaction {
    const isValid = tx && typeof tx === 'object' && 
        tx.data && tx.to && 
        typeof tx.data === 'string' && 
        typeof tx.to === 'string';
    
    if (!isValid) {
        throw new Error("Invalid input: Must be an object with 'data' and 'to' fields of type string");
    }

    const txData = utils.remove0xPrefixIfNeeded(tx.data);
    const txTo = utils.remove0xPrefixIfNeeded(tx.to);

    // Validate that the data and to fields contain only hex characters and are of even length
    utils.validateHexString(txData);
    utils.validateHexString(txTo);

    if (txTo.length !== 40) {
        throw new Error("Invalid input: 'to' field must be exactly 20 bytes (40 hex characters) long");
    }

    return { data: txData, to: txTo };
}

/**
 * RLP encodes transaction data and to address
 * @param {string} txTo - The transaction to address as hex string (without 0x prefix)
 * @param {string} txData - The transaction data as hex string (without 0x prefix)
 * @returns {string} RLP encoded data as hex string (without 0x prefix)
 */
function rlpEncodeTransactionData(txTo: string, txData: string): string {
    try {
        // Convert hex strings to Buffer for RLP encoding
        const toBuffer = Buffer.from(txTo, 'hex');
        const dataBuffer = Buffer.from(txData, 'hex');
        
        // RLP encode as array [txData, txTo]
        const rlpEncoded = encode([dataBuffer, toBuffer]);
        
        // Convert back to hex string without 0x prefix
        return Buffer.from(rlpEncoded).toString('hex');
    } catch (error) {
        logger.error('Error RLP encoding transaction data:', error);
        throw new Error('Failed to RLP encode transaction data');
    }
}

/**
 * RLP encodes array of epochId and encrypted message
 * @param {(number | Buffer | (number | Buffer)[])[]} data - Array of data to RLP encode
 * @returns {string} RLP encoded data as hex string (without 0x prefix)
 */
function rlpEncodeMessageData(data: (number | Buffer | (number | Buffer)[])[]): string {
    try {
        // RLP encode the array
        const rlpEncoded = encode(data);
        
        // Convert back to hex string without 0x prefix
        return Buffer.from(rlpEncoded).toString('hex');
    } catch (error) {
        logger.error('Error RLP encoding message data:', error);
        throw new Error('Failed to RLP encode message data');
    }
}